
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';

export default function Fees() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('trading');

  const feeStructure = {
    trading: [
      { type: 'Market Orders', fee: '0.1%', description: 'Instant execution at current market price' },
      { type: 'Limit Orders', fee: '0.05%', description: 'Execute when price reaches your target' },
      { type: 'Stop Loss Orders', fee: '0.1%', description: 'Automatic sell when price drops' },
      { type: 'Advanced Orders', fee: '0.15%', description: 'OCO, Trailing stop, and other advanced orders' },
    ],
    p2p: [
      { type: 'P2P Trading', fee: '0.5%', description: 'Per transaction (both buyer and seller)' },
      { type: 'Escrow Service', fee: 'Included', description: 'Secure transaction protection' },
      { type: 'Dispute Resolution', fee: 'Free', description: 'Professional mediation service' },
      { type: 'Express Trading', fee: '1%', description: 'Priority matching and faster settlement' },
    ],
    wallet: [
      { type: 'PEP Transfers', fee: '$0.25', description: 'Internal PepCoin network transfers' },
      { type: 'Bitcoin (BTC)', fee: '$8.50', description: 'Bitcoin network fee (dynamic)' },
      { type: 'Ethereum (ETH)', fee: '$12.30', description: 'Ethereum network fee (dynamic)' },
      { type: 'USDT (ERC-20)', fee: '$15.00', description: 'Ethereum-based USDT transfers' },
      { type: 'Platform Fee', fee: '0.1%', description: 'Additional platform processing fee' },
    ],
    mining: [
      { type: 'Pool Fee', fee: '2%', description: 'Automatically deducted from mining rewards' },
      { type: 'Withdrawal Fee', fee: '0.5%', description: 'When withdrawing mined PEP (min $1)' },
      { type: 'Cloud Mining', fee: '5%', description: 'Managed mining service fee' },
      { type: 'Hardware Rental', fee: '10%/month', description: 'Professional mining equipment rental' },
    ],
    staking: [
      { type: 'Staking Rewards', fee: 'Free', description: 'No fees on staking PEP tokens' },
      { type: 'Unstaking Fee', fee: '2%', description: 'Early withdrawal penalty' },
      { type: 'Compound Rewards', fee: 'Free', description: 'Automatic reward reinvestment' },
      { type: 'Flexible Staking', fee: '0.5%', description: 'No lock-up period staking' },
    ],
    premium: [
      { type: 'Basic Plan', fee: 'Free', description: 'Standard trading features' },
      { type: 'Pro Plan', fee: '$29/month', description: 'Advanced analytics and lower fees' },
      { type: 'VIP Plan', fee: '$99/month', description: 'Premium features and priority support' },
      { type: 'Enterprise', fee: 'Custom', description: 'Institutional-grade solutions' },
    ],
  };

  const discountTiers = [
    { tier: 'Bronze', volume: '$0 - $10K', discount: '0%', pepHolding: '0 PEP' },
    { tier: 'Silver', volume: '$10K - $50K', discount: '10%', pepHolding: '1,000 PEP' },
    { tier: 'Gold', volume: '$50K - $200K', discount: '20%', pepHolding: '5,000 PEP' },
    { tier: 'Platinum', volume: '$200K - $1M', discount: '30%', pepHolding: '25,000 PEP' },
    { tier: 'Diamond', volume: '$1M+', discount: '50%', pepHolding: '100,000 PEP' },
  ];

  return (
    <div className="min-h-screen bg-black">
      <Navbar />

      <div className="pt-16 px-4 pb-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="w-10 h-10 flex items-center justify-center bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition-all duration-300 cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-white">Fee Structure</h1>
                <p className="text-gray-400 text-sm">Transparent and competitive pricing</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/trading')}
                className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-lg text-white text-sm hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-exchange-line mr-2"></i>
                Start Trading
              </button>
            </div>
          </div>

          {/* Fee Overview Cards */}
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <div className="p-6 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-2xl">
              <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-exchange-line text-2xl text-cyan-400"></i>
              </div>
              <h3 className="text-white font-bold text-lg mb-2">Trading Fees</h3>
              <p className="text-cyan-400 text-2xl font-bold mb-1">0.05%</p>
              <p className="text-gray-400 text-sm">Starting from (Limit Orders)</p>
            </div>

            <div className="p-6 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-2xl">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-group-line text-2xl text-purple-400"></i>
              </div>
              <h3 className="text-white font-bold text-lg mb-2">P2P Trading</h3>
              <p className="text-purple-400 text-2xl font-bold mb-1">0.5%</p>
              <p className="text-gray-400 text-sm">Per transaction</p>
            </div>

            <div className="p-6 bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-2xl">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-cpu-line text-2xl text-green-400"></i>
              </div>
              <h3 className="text-white font-bold text-lg mb-2">Mining Pool</h3>
              <p className="text-green-400 text-2xl font-bold mb-1">2%</p>
              <p className="text-gray-400 text-sm">Pool fee</p>
            </div>

            <div className="p-6 bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-2xl">
              <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-wallet-3-line text-2xl text-orange-400"></i>
              </div>
              <h3 className="text-white font-bold text-lg mb-2">PEP Transfers</h3>
              <p className="text-orange-400 text-2xl font-bold mb-1">$0.25</p>
              <p className="text-gray-400 text-sm">Fixed fee</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="overflow-x-auto mb-8">
            <div className="flex gap-2 bg-white/5 p-1 rounded-xl w-fit min-w-full">
              {[
                { id: 'trading', label: 'Trading', icon: 'ri-line-chart-line' },
                { id: 'p2p', label: 'P2P', icon: 'ri-group-line' },
                { id: 'wallet', label: 'Wallet', icon: 'ri-wallet-3-line' },
                { id: 'mining', label: 'Mining', icon: 'ri-cpu-line' },
                { id: 'staking', label: 'Staking', icon: 'ri-percent-line' },
                { id: 'premium', label: 'Premium', icon: 'ri-vip-crown-line' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                    activeTab === tab.id
                      ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                      : 'text-gray-400 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <i className={tab.icon}></i>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Fee Details */}
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                <h2 className="text-xl font-bold text-white mb-6 capitalize">{activeTab} Fees</h2>
                
                <div className="space-y-4">
                  {feeStructure[activeTab as keyof typeof feeStructure].map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-black/30 rounded-xl hover:bg-black/40 transition-all duration-300">
                      <div className="flex-1">
                        <h3 className="text-white font-semibold mb-1">{item.type}</h3>
                        <p className="text-gray-400 text-sm">{item.description}</p>
                      </div>
                      <div className="text-right">
                        <div className={`text-lg font-bold ${
                          item.fee === 'Free' || item.fee === 'Included' 
                            ? 'text-green-400' 
                            : item.fee === 'Custom'
                            ? 'text-purple-400'
                            : 'text-cyan-400'
                        }`}>
                          {item.fee}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-6">
              {/* Discount Tiers */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">VIP Discount Tiers</h3>
                
                <div className="space-y-3">
                  {discountTiers.map((tier, idx) => (
                    <div key={idx} className={`p-3 rounded-lg border ${
                      tier.tier === 'Diamond' 
                        ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-purple-500/30'
                        : tier.tier === 'Platinum'
                        ? 'bg-gradient-to-r from-gray-400/20 to-gray-600/20 border-gray-400/30'
                        : 'bg-black/30 border-white/10'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`font-semibold ${
                          tier.tier === 'Diamond' ? 'text-purple-400' :
                          tier.tier === 'Platinum' ? 'text-gray-300' :
                          tier.tier === 'Gold' ? 'text-yellow-400' :
                          tier.tier === 'Silver' ? 'text-gray-400' :
                          'text-orange-400'
                        }`}>
                          {tier.tier}
                        </span>
                        <span className="text-green-400 font-bold">{tier.discount} OFF</span>
                      </div>
                      <div className="text-xs text-gray-400 space-y-1">
                        <div>Volume: {tier.volume}</div>
                        <div>Hold: {tier.pepHolding}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Fee Calculator */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">Fee Calculator</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Trade Amount ($)</label>
                    <input
                      type="number"
                      placeholder="1000"
                      className="w-full p-3 bg-black/30 border border-white/20 rounded-lg text-white text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Order Type</label>
                    <select className="w-full p-3 bg-black/30 border border-white/20 rounded-lg text-white text-sm pr-8">
                      <option value="market">Market Order (0.1%)</option>
                      <option value="limit">Limit Order (0.05%)</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">VIP Tier</label>
                    <select className="w-full p-3 bg-black/30 border border-white/20 rounded-lg text-white text-sm pr-8">
                      <option value="bronze">Bronze (0% discount)</option>
                      <option value="silver">Silver (10% discount)</option>
                      <option value="gold">Gold (20% discount)</option>
                      <option value="platinum">Platinum (30% discount)</option>
                      <option value="diamond">Diamond (50% discount)</option>
                    </select>
                  </div>
                  
                  <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-400">Base Fee:</span>
                      <span className="text-white">$1.00</span>
                    </div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-400">Discount:</span>
                      <span className="text-green-400">-$0.00</span>
                    </div>
                    <div className="flex justify-between text-sm font-bold border-t border-cyan-500/30 pt-1">
                      <span className="text-cyan-400">Total Fee:</span>
                      <span className="text-cyan-400">$1.00</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Special Offers */}
              <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">Special Offers</h3>
                
                <div className="space-y-3">
                  <div className="p-3 bg-green-500/20 border border-green-500/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <i className="ri-gift-line text-green-400"></i>
                      <span className="text-green-400 font-semibold text-sm">New User Bonus</span>
                    </div>
                    <p className="text-gray-300 text-xs">Zero fees for first 30 days</p>
                  </div>
                  
                  <div className="p-3 bg-purple-500/20 border border-purple-500/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <i className="ri-vip-crown-line text-purple-400"></i>
                      <span className="text-purple-400 font-semibold text-sm">VIP Upgrade</span>
                    </div>
                    <p className="text-gray-300 text-xs">50% off fees with 10,000+ PEP</p>
                  </div>
                  
                  <div className="p-3 bg-cyan-500/20 border border-cyan-500/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <i className="ri-group-line text-cyan-400"></i>
                      <span className="text-cyan-400 font-semibold text-sm">Referral Bonus</span>
                    </div>
                    <p className="text-gray-300 text-xs">Earn 25% of referred user fees</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
